import axiosInstance from '../api';
import { DEMO_MODE } from '../../config';
import { SessionManager } from '../../utils/SessionManager';
import { resolveSpecialtyAttributes } from '../../utils/specialtyAttributes';
import { getDemoStreamUrl } from '../../demo/demoData';
import type {
  Video,
  VideoResponse,
  SingleVideoResponse,
  VideoUploadReq,
  VideoUploadRes,
  VideoUpdateReq,
  VideoUpdateRes,
  VideoTrimReq,
  VideoTrimRes,
  VideoSearchOptions,
  SpecialtiesRes,
  SearchSuggestion,
  Specialty,
  SpecialtyAttributeKey,
  SpecialtyCountsRes,
} from './videoApi.types';

/**
 * 全ての動画を取得する（必要に応じて利用）
 * @returns 動画データの配列
 */
export const fetchVideos = async (): Promise<Video[]> => {
  const response = await axiosInstance.get<Video[]>('/videos');
  return response.data;
};

/**
 * ページネーション対応の動画一覧を取得する
 * @param page ページ番号（デフォルト: 1）
 * @param limit 1ページあたりの件数（デフォルト: 10）
 * @param sortBy ソート項目（デフォルト: 'publish_date'）
 * @param sortOrder ソート順（デフォルト: 'DESC'）
 * @returns ページネーション情報付きの動画データ
 */
export const fetchPaginatedVideos = async (
  page = 1,
  limit = 10,
  sortBy = 'publish_date',
  sortOrder = 'DESC',
  hospitalOnly = false,
  specialty?: string
): Promise<VideoResponse> => {
  const response = await axiosInstance.get<VideoResponse>('/videos/paginated', {
    params: {
      page,
      limit,
      sortBy,
      sortOrder,
      hospital_only: hospitalOnly,
      // 診療科ID または UNCLASSIFIED_SPECIALTY_KEY。未指定なら送らない
      ...(specialty ? { specialty } : {})
    }
  });
  return response.data;
};

/**
 * 一覧の診療科フィルタに出す候補を取得する。
 * いま見える動画が1本以上ある診療科だけが返る（空の科は並ばない）。
 * @param hospitalOnly 院内限定の動画も数えるか
 */
export const fetchSpecialtyCounts = async (hospitalOnly = false): Promise<SpecialtyCountsRes> => {
  // 失敗はそのまま投げ、報告は呼び出し側（フック）に任せる
  const response = await axiosInstance.get<SpecialtyCountsRes>('/videos/specialty-counts', {
    params: { hospital_only: hospitalOnly }
  });
  return {
    specialties: response.data?.specialties ?? [],
    unclassified_count: response.data?.unclassified_count ?? 0
  };
};

/**
 * IDを指定して単一の動画を取得する（デフォルトでは視聴カウントは加算しない）
 * @param id 動画ID
 * @returns 動画データ
 */
export const fetchVideoById = async (id: string | number): Promise<SingleVideoResponse> => {
  const response = await axiosInstance.get<SingleVideoResponse>(`/videos/${id}`);
  // view_countとviewsの同期（互換性のため）
  if (response.data && response.data.view_count && !response.data.views) {
    response.data.views = response.data.view_count;
  }
  return response.data;
};

/**
 * 再生回数をインクリメントする（視聴カウント付きで動画を取得）
 * @param id 動画ID
 * @returns 更新された動画データ
 */
export const incrementViewCount = async (id: string | number): Promise<SingleVideoResponse> => {
  const response = await axiosInstance.get<SingleVideoResponse>(`/videos/${id}?play=true`);
  // view_countとviewsの同期（互換性のため）
  if (response.data && response.data.view_count && !response.data.views) {
    response.data.views = response.data.view_count;
  }
  return response.data;
};

/**
 * 動画ストリーミングURLを取得する
 * @param videoId 動画ID
 * @returns ストリーミングURL
 */
export const getVideoStreamUrl = (videoId: string | number): string => {
  // デモモード時は public/demo/ 配下のサンプル動画を返す
  if (DEMO_MODE) {
    return getDemoStreamUrl(videoId);
  }

  // SessionManagerからトークンを取得
  const activeSession = SessionManager.getActiveSession();
  const token = activeSession?.token;
  
  // トークンをクエリパラメータとして追加
  if (token) {
    return `/api/videos/stream/${videoId}?token=${token}`;
  }

  // トークンがない場合はURLのみ返す（バックエンドで401エラーになる可能性あり）
  return `/api/videos/stream/${videoId}`;
};

/**
 * 動画をアップロードする
 * @param formData アップロードするフォームデータ
 * @returns アップロード結果
 */
export const uploadVideo = async (formData: FormData): Promise<VideoUploadRes> => {
  try {
    const response = await axiosInstance.post<VideoUploadRes>('/videos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading video:', error);
    throw error;
  }
};

/**
 * 動画を削除する
 * @param id 削除する動画ID
 * @returns 削除結果
 */
export const deleteVideo = async (id: string | number): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axiosInstance.delete<{ success: boolean; message?: string }>('/videos', {
      params: { id }
    });
    return response.data;
  } catch (error) {
    console.error(`Error deleting video with id ${id}:`, error);
    throw error;
  }
};

/**
 * 動画情報を更新する
 * @param id 更新する動画ID
 * @param data 更新データ
 * @returns 更新結果
 */
export const updateVideo = async (id: string | number, data: VideoUpdateReq): Promise<VideoUpdateRes> => {
  try {
    const response = await axiosInstance.put<VideoUpdateRes>(`/videos/${id}`, data, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating video with id ${id}:`, error);
    throw error;
  }
};

/**
 * 動画をトリミングする
 * @param videoId トリミングする動画ID
 * @param startTime 開始時間（秒）
 * @param endTime 終了時間（秒）
 * @returns トリミング結果
 */
export const trimVideo = async (
  videoId: string | number,
  startTime: number,
  endTime: number
): Promise<VideoTrimRes> => {
  try {
    const body: VideoTrimReq = { videoId, startTime, endTime };
    const response = await axiosInstance.post<VideoTrimRes>('/videos/trim', body, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    console.error('Error trimming video:', error);
    throw error;
  }
};

/**
 * 動画を検索する（部分一致検索、ページネーション対応）
 * @param query 検索クエリ
 * @param page ページ番号（デフォルト: 1）
 * @param limit 1ページあたりの件数（デフォルト: 20）
 * @param sortBy ソート項目（デフォルト: 'publish_date'）
 * @param sortOrder ソート順（デフォルト: 'DESC'）
 * @returns 検索結果
 */
export const searchVideos = async (
  query: string,
  page = 1,
  limit = 20,
  sortBy = 'publish_date',
  sortOrder = 'DESC',
  hospitalOnly = false,
  specialty?: string
): Promise<VideoResponse> => {
  try {
    // 文字列以外のデータが渡された場合は空文字列にする
    const safeQuery = typeof query === 'string' ? query : '';

    // 病院:XXXのような特殊検索の場合のチェック
    const isHospitalSearch = safeQuery.startsWith('病院:');

    const response = await axiosInstance.get<VideoResponse>('/videos/search', {
      params: {
        q: safeQuery,
        page,
        limit,
        sortBy,
        sortOrder,
        hospital_only: hospitalOnly,
        ...(specialty ? { specialty } : {})
      }
    });
    
    // APIからのレスポンスを検証
    if (response.data && response.data.videos) {
      // 病院検索で結果が0件の場合はそのまま返す
      if (isHospitalSearch && response.data.videos.length === 0) {
        return response.data;
      }
      
      // 結果を確認（データが壊れていないか）
      const validVideos = response.data.videos.filter((video: any) => 
        video && video.uploader && typeof video.uploader === 'object'
      );
      
      // viewsを追加（互換性のため）
      validVideos.forEach((video: any) => {
        if (video.view_count && !video.views) {
          video.views = video.view_count;
        }
      });
      
      // 有効なデータがない場合は空のレスポンスを返す
      if (isHospitalSearch && validVideos.length === 0) {
        return {
          videos: [],
          pagination: {
            totalItems: 0,
            currentPage: page,
            totalPages: 0,
            pageSize: limit
          }
        };
      }
      
      // 有効なデータのみを返す
      return {
        ...response.data,
        videos: validVideos
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error searching videos:', error);
    // エラー時でも一貫したデータ構造を返す
    return {
      videos: [],
      pagination: {
        totalItems: 0,
        currentPage: page,
        totalPages: 0,
        pageSize: limit
      }
    };
  }
};

/**
 * 軽量サジェスト用API（動画のidとtitleのみ返す）
 * @param query 検索クエリ
 * @returns サジェスト結果（軽量版）
 */
export const suggestVideos = async (query: string): Promise<SearchSuggestion[]> => {
  if (!query) return [];
  try {
    const response = await axiosInstance.get<SearchSuggestion[]>(
      '/videos/suggestions', 
      { params: { q: query } }
    );
    
    // バックエンドから返されるデータをそのまま返す
    return response.data || [];
  } catch (error) {
    console.error('Error suggesting videos:', error);
    throw error;
  }
};

/**
 * 診療科一覧を取得する
 * @returns 診療科一覧
 */
export const fetchSpecialties = async (): Promise<Specialty[]> => {
  try {
    const response = await axiosInstance.get<SpecialtiesRes | Specialty[]>('/specialties');
    // 本番APIは配列をそのまま返し、デモモードは { specialties: [...] } を返す。両方受ける。
    // 配列側を読み落とすと診療科セレクトが常に空になり、公開に必要な診療科を選べなくなる
    const data = response.data;
    return Array.isArray(data) ? data : (data?.specialties ?? []);
  } catch (error) {
    console.error('Error fetching specialties:', error);
    throw error;
  }
};

/**
 * 診療科ごとの属性キー候補を取得する（タグ入力のサジェスト用）
 * @param specialtyId 診療科ID
 * @returns 表示順に並んだ属性キーの配列
 */
export const fetchSpecialtyAttributeKeys = async (specialtyId: string | number): Promise<string[]> => {
  try {
    const response = await axiosInstance.get<SpecialtyAttributeKey[]>(`/specialties/${specialtyId}/attribute-keys`);
    return (response.data || []).map(key => key.attribute_key);
  } catch (error) {
    console.error(`Error fetching attribute keys for specialty ${specialtyId}:`, error);
    throw error;
  }
};

/**
 * 自分の動画のみをリストする
 * @param options 取得オプション
 * @returns 自分の動画一覧
 */
export const fetchMyVideos = async (options: VideoSearchOptions = {}): Promise<VideoResponse> => {
  try {
    const { 
      page = 1, 
      limit = 10, 
      status = 'all', 
      sortBy = 'created_at', 
      sortOrder = 'DESC',
      sort_by, // 互換性のため追加
      sort_order, // 互換性のため追加
    } = options;
    
    // sortByとsort_byの共存
    const finalSortBy = sort_by || sortBy;
    const finalSortOrder = sort_order || sortOrder;
    
    const params = { page, limit, status, sortBy: finalSortBy, sortOrder: finalSortOrder };
    const response = await axiosInstance.get<VideoResponse>('/videos/my', { params });
    
    // レスポンスの形式を確認して一貫性を保つ
    const data = response.data || {};
    
    // pagination情報がない場合のデフォルト値を設定
    if (!data.pagination) {
      data.pagination = {
        totalItems: (data.videos || []).length,
        currentPage: page,
        totalPages: 1,
        pageSize: limit
      };
    }
    
    // videosがない場合は空配列を返す
    if (!data.videos) {
      data.videos = [];
    } else {
      // viewsを追加（互換性のため）
      data.videos.forEach((video: any) => {
        if (video.view_count && !video.views) {
          video.views = video.view_count;
        }
      });
    }
    
    return data;
  } catch (error) {
    console.error('動画取得エラー:', error);
    // エラー時でも一貫したデータ構造を返す
    return {
      videos: [],
      pagination: {
        totalItems: 0,
        currentPage: options.page || 1,
        totalPages: 0,
        pageSize: options.limit || 10
      }
    };
  }
};

/**
 * 自分のお気に入り動画のみをリストする
 * @param options 取得オプション
 * @returns お気に入り動画一覧
 */
export const fetchFavoriteVideos = async (options: VideoSearchOptions = {}): Promise<VideoResponse> => {
  try {
    const { 
      page = 1, 
      limit = 10, 
      sortBy = 'created_at',
      sortOrder = 'DESC',
      search_query = '',
      sort_by, // 互換性のため追加
      sort_order, // 互換性のため追加
    } = options;
    
    // sortByとsort_byの共存
    const finalSortBy = sort_by || sortBy;
    const finalSortOrder = sort_order || sortOrder;
    
    // お気に入り動画一覧を取得
    const response = await axiosInstance.get<VideoResponse>('/videos/favorite', {
      params: { 
        page, 
        limit, 
        sort_by: finalSortBy, 
        sort_order: finalSortOrder,
        search_query 
      }
    });
    
    // レスポンスの形式を確認して一貫性を保つ
    const data = response.data || {};
    
    // pagination情報がない場合のデフォルト値を設定
    if (!data.pagination) {
      data.pagination = {
        totalItems: (data.videos || []).length,
        currentPage: page,
        totalPages: 1,
        pageSize: limit
      };
    } else {
      // pagination.totalItemsが数値であることを確認する
      data.pagination.totalItems = Number(data.pagination.totalItems);
      data.pagination.currentPage = Number(data.pagination.currentPage);
      data.pagination.totalPages = Number(data.pagination.totalPages);
    }
    
    // videosがない場合は空配列を返す
    if (!data.videos) {
      data.videos = [];
    } else {
      // viewsを追加（互換性のため）
      data.videos.forEach((video: any) => {
        if (video.view_count && !video.views) {
          video.views = video.view_count;
        }
      });
    }
    
    return data;
  } catch (error) {
    console.error('お気に入り動画取得エラー:', error);
    // エラー時でも一貫したデータ構造を返す
    return {
      videos: [],
      pagination: {
        totalItems: 0,
        currentPage: options.page || 1,
        totalPages: 0,
        pageSize: options.limit || 10
      }
    };
  }
};

/**
 * いいねした動画を取得する
 * @param options 取得オプション
 * @returns いいねした動画一覧
 */
export const fetchLikedVideos = async (options: VideoSearchOptions = {}): Promise<VideoResponse> => {
  try {
    const { 
      page = 1, 
      limit = 10, 
      sortBy = 'created_at',
      sortOrder = 'DESC',
      search_query = '',
      sort_by, // 互換性のため追加
      sort_order, // 互換性のため追加
    } = options;
    
    // sortByとsort_byの共存
    const finalSortBy = sort_by || sortBy;
    const finalSortOrder = sort_order || sortOrder;
    
    const response = await axiosInstance.get<VideoResponse>('/videos/liked', {
      params: { 
        page, 
        limit, 
        sort_by: finalSortBy, 
        sort_order: finalSortOrder,
        search_query 
      }
    });
    
    // レスポンスの形式を確認して一貫性を保つ
    const data = response.data || {};
    
    // pagination情報がない場合のデフォルト値を設定
    if (!data.pagination) {
      data.pagination = {
        totalItems: (data.videos || []).length,
        currentPage: page,
        totalPages: 1,
        pageSize: limit
      };
    } else {
      // pagination.totalItemsが数値であることを確認する
      data.pagination.totalItems = Number(data.pagination.totalItems);
      data.pagination.currentPage = Number(data.pagination.currentPage);
      data.pagination.totalPages = Number(data.pagination.totalPages);
    }
    
    // videosがない場合は空配列を返す
    if (!data.videos) {
      data.videos = [];
    } else {
      // viewsを追加（互換性のため）
      data.videos.forEach((video: any) => {
        if (video.view_count && !video.views) {
          video.views = video.view_count;
        }
      });
    }
    
    return data;
  } catch (error) {
    console.error('いいね動画取得エラー:', error);
    // エラー時でも一貫したデータ構造を返す
    return {
      videos: [],
      pagination: {
        totalItems: 0,
        currentPage: options.page || 1,
        totalPages: 0,
        pageSize: options.limit || 10
      }
    };
  }
};

/**
 * 関連動画を取得する
 * @param video 基準となる動画オブジェクト
 * @returns 関連動画一覧
 */
export const fetchRelatedVideos = async (video: Video): Promise<{ videos: Video[] }> => {
  let department = '';
  let tags: string[] = [];
  
  // videoが存在しない場合は空の配列を返す
  if (!video || !video.id) {
    return { videos: [] };
  }
  
  // 現在の動画ID（発火元の動画ID）
  const current_video_id = video.id;
  
  // specialtyから診療科名（department）を取得
  if (video?.specialty?.name) {
    department = video.specialty.name;
  }
  
  // 関連動画の手がかりは、診療科を問わずキー付きタグ（部位:膝 など）の値。
  // 属性が無い動画はフリータグ（video.tags）を使う
  const attributeValues = resolveSpecialtyAttributes(video).map(attribute => attribute.attribute_value);
  if (attributeValues.length > 0) {
    tags = attributeValues;
  } else if (video.tags) {
    // video.tagsにカンマ区切りでタグが記述されているケースに対応
    tags = video.tags.split(',').map((tag: string) => tag.trim()).filter(Boolean);
  }
  
  try {
    // axiosInstance を用いて /videos/related エンドポイントに対し query パラメータを送信
    const response = await axiosInstance.get<{ videos: Video[] }>('/videos/related', {
      params: { department, tags, current_video_id }
    });
    
    // viewsを追加（互換性のため）
    if (response.data && response.data.videos) {
      response.data.videos.forEach((video: any) => {
        if (video.view_count && !video.views) {
          video.views = video.view_count;
        }
      });
    }
    
    return response.data;
  } catch (error) {
    console.error('関連動画の取得中にエラーが発生しました:', error);
    return { videos: [] };
  }
};

/**
 * 処理中の動画（アップロードセッション）を削除する
 * @param sessionId セッションID（session_プレフィックス付きの場合は自動で除去）
 * @returns 削除結果
 */
export const deleteProcessingVideo = async (sessionId: string): Promise<{ success: boolean; message?: string }> => {
  try {
    // session_プレフィックスがある場合は除去
    const cleanSessionId = sessionId.startsWith('session_')
      ? sessionId.replace('session_', '')
      : sessionId;

    const response = await axiosInstance.delete(`/videos/upload/${cleanSessionId}`);
    return { success: true, message: response.data.message };
  } catch (error) {
    console.error('処理中動画の削除に失敗しました:', error);
    throw error;
  }
}; 
